import { useMergeDatasetsMutation } from "graphql/types/graphql";
import { closeSnackbar, enqueueSnackbar } from "notistack";
import { useContext } from "react";
import { context } from "utils/Provider";
import { v4 as uuidv4 } from "uuid";

export type Dataset = {
  id: string;
  name: string;
};

export const useMergeHandler = (
  cubeId: string,
  setOpenedDatasetIds: React.Dispatch<React.SetStateAction<string[]>>,
  setSelectedColumns: React.Dispatch<
    React.SetStateAction<
      Record<
        string,
        { selectedColumns: string[]; joinColumn: string | null; joinWith?: string; joinWithColumn?: string | null }
      >
    >
  >,
  setTableOrder: React.Dispatch<React.SetStateAction<string[]>>,
  setOutputTableName: React.Dispatch<React.SetStateAction<string>>,
  isReplacingView: boolean
) => {
  const [mergeDatasets] = useMergeDatasetsMutation({
    update: (cache) => {
      cache.evict({
        id: "ROOT_QUERY",
        fieldName: "views",
      });
      cache.evict({
        id: "ROOT_QUERY",
        fieldName: "datasetsByCubeId",
      });
    },
  });
  const { setIsLoading } = useContext(context);

  const handleMerge = async (
    outputTableName: string,
    selectedColumns: Record<
      string,
      { selectedColumns: string[]; joinColumn: string | null; joinWith?: string; joinWithColumn?: string | null }
    >,
    tableOrder: string[],
    datasets: Dataset[]
  ) => {
    if (!outputTableName.trim()) {
      enqueueSnackbar("Please specify an output table name.", { variant: "error" });
      return;
    }

    const isValidSelection = Object.entries(selectedColumns).every(([datasetId, value]) => {
      const isFirstTable = tableOrder[0] === datasetId;
      return (
        value.selectedColumns &&
        value.selectedColumns.length > 0 &&
        (isFirstTable || (value.joinColumn && value.joinWith && value.joinWithColumn))
      );
    });
    if (!isValidSelection) {
      enqueueSnackbar(
        "Each dataset must have at least one column selected. Non-first datasets must have a join column, join with dataset, and join by column selected.",
        { variant: "error", autoHideDuration: 8000 }
      );
      return;
    }

    const hasUnselectedDatasets = tableOrder.some((datasetId) => !Object.keys(selectedColumns).includes(datasetId));
    if (hasUnselectedDatasets) {
      enqueueSnackbar("All datasets in the table order must have at least one column selected.", {
        variant: "error",
        autoHideDuration: 5000,
      });
      return;
    }

    const validMergeInputs = Object.entries(selectedColumns)
      .filter(([datasetId, { joinColumn, joinWith, joinWithColumn }]) => {
        const isFirstTable = tableOrder[0] === datasetId;
        return isFirstTable || (joinColumn && joinWith && joinWithColumn && selectedColumns[joinWith]);
      })
      .map(([datasetId, { selectedColumns, joinColumn, joinWith, joinWithColumn }]) => ({
        datasetId,
        columnName: joinColumn || "",
        selectedColumns,
        joinWith: tableOrder[0] === datasetId ? "none" : joinWith,
        joinWithColumn: joinWithColumn || "",
        datasetName: datasets.find((dataset) => dataset.id === datasetId)?.name || "Unknown Dataset",
      }))
      .sort((a, b) => tableOrder.indexOf(a.datasetId) - tableOrder.indexOf(b.datasetId));

    const missingJoinWith = validMergeInputs.some((input, index) => {
      if (index === 0) return false;
      const datasetName = input.datasetName;
      return !input.joinWith || input.joinWith === "none" || !input.joinWithColumn
        ? (enqueueSnackbar(
            `Dataset '${datasetName}' must have a valid 'Join With' dataset and 'Join Column (Join With)' selected.`,
            { variant: "error", autoHideDuration: 5000 }
          ),
          true)
        : false;
    });
    if (missingJoinWith) {
      return;
    }

    if (validMergeInputs.some((input) => input.datasetId === input.joinWith)) {
      enqueueSnackbar("A dataset cannot join with itself.", { variant: "error" });
      return;
    }

    const invalidJoinWith = validMergeInputs.some((input, index) => {
      if (index === 0) return false;
      const isInvalid = input.joinWith && input.joinWith !== "none" && !tableOrder.includes(input.joinWith);
      if (isInvalid) {
        enqueueSnackbar(`Dataset '${input.datasetName}' has an invalid 'Join With' dataset not in table order.`, {
          variant: "error",
        });
      }
      return isInvalid;
    });
    if (invalidJoinWith) {
      return;
    }

    const hasForwardJoins = validMergeInputs.some((input, idx) => {
      if (idx === 0) return false;
      const joinWithIndex = input.joinWith ? tableOrder.indexOf(input.joinWith) : -1;
      const isForward = joinWithIndex > idx;
      if (isForward) {
        enqueueSnackbar(
          `Dataset '${input.datasetName}' cannot join with '${
            datasets.find((d) => d.id === input.joinWith)?.name
          }' as it appears later in the table order.`,
          { variant: "error", autoHideDuration: 5000 }
        );
      }
      return isForward;
    });
    if (hasForwardJoins) {
      return;
    }

    const snackbarKey = enqueueSnackbar("Merging is in progress...", { variant: "info", persist: true });
    const newDatasetId = uuidv4();
    try {
      setIsLoading(true);
      const response = await mergeDatasets({
        variables: {
          payload: {
            mergeInputs: validMergeInputs,
            outputTableName,
            cubeId,
            tableOrder,
            isReplacingView: isReplacingView,
            datasetId: newDatasetId,
          },
        },
      });

      closeSnackbar(snackbarKey);

      if (response.data?.mergeDatasets.success) {
        enqueueSnackbar(`Datasets merged successfully`, { variant: "success" });
        setOpenedDatasetIds([]);
        setSelectedColumns({});
        setTableOrder([]);
        setOutputTableName("");
      } else {
        enqueueSnackbar(`Merge failed: ${response.data?.mergeDatasets.error}`, { variant: "error" });
      }
    } catch (error) {
      closeSnackbar(snackbarKey);
      enqueueSnackbar(
        `An error occurred while merging datasets: ${error instanceof Error ? error.message : "Something went wrong"}`,
        { variant: "error" }
      );
    } finally {
      setIsLoading(false);
    }
  };

  return { handleMerge };
};
